import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom, type Atom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { createContext } from 'react';

import { calcPositionMargin } from '@flipster/features/src/shared/calculator/adjust-margin';
import { pnlModeBunja } from '@flipster/features/src/shared/pnl-setting-modal/state';
import { getDisplayPnl, getDisplayPnlPercent } from '@flipster/features/src/shared/pnl/get-display-pnl';
import { snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { SymbolScope } from '@flipster/features/src/shared/symbol/bunja';
import { upnlBasisBunja } from '@flipster/features/src/shared/upnl-basis/state';
import { openPositionsBunja, type OpenPosition } from '@flipster/r230522/original/position';
import { getLocalStorageAtom } from '@flipster/react/misc/state/use-local-storage';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber } from '@flipster/utils/num';

import { formatLeverageDisplay } from '../../useDisplayedLeverage';
import { isShallowStableEqual } from '../position-identity';

// 행이 담당하는 포지션 slot 주입용 스코프 (심볼은 기존 SymbolScope 재사용)
export const PositionSlotContext = createContext<number>(null as unknown as number);
export const PositionSlotScope = createScopeFromContext(PositionSlotContext);

/**
 * 열린 포지션 행 하나의 데이터 레이어. (SymbolScope, PositionSlotScope)로 스코프되며,
 * 행 컴포넌트가 Context Provider로 스코프를 주입하므로 셀은 인자 없이 useBunja로 consume한다.
 *
 * `openPositionsAtom`이 소켓 tick마다 포지션 객체를 새로 만들어도, 필드 값이 전부 같으면
 * 이전 객체 identity를 유지해(isShallowStableEqual) 값이 실제로 바뀐 tick에만 전파된다.
 * 셀은 자기 필드 atom만 구독하므로 해당 필드가 바뀐 tick에만 리렌더된다.
 *
 * rAF 스로틀은 없으므로 이 atom들의 값은 source 그대로다 — 액션 핸들러에서 그대로 사용해도 된다.
 */
export const openPositionRowDataBunja = bunja(() => {
  const symbol = bunja.use(SymbolScope);
  const slot = bunja.use(PositionSlotScope);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { snapshotSpecAtom } = bunja.use(snapshotSpecBunja);
  const { pnlModeAtom } = bunja.use(pnlModeBunja);
  const { upnlBasisAtom } = bunja.use(upnlBasisBunja);

  const positionAtom: Atom<OpenPosition | undefined> = selectAtom(
    atom((get) => get(openPositionsAtom)?.[symbol]?.find((position) => position.slot === slot)),
    (position) => position,
    isShallowStableEqual,
  );

  // 주의: snapshotSpecAtom은 positionBracket(마켓 데이터)에 의존해 매 tick 재계산되며 instrument 객체를
  // 통째로 새로 만든다. 따라서 instrumentAtom은 identity가 매 tick 바뀐다 → 객체를 그대로 구독하면
  // 표시값이 안 바뀐 tick에도 리렌더된다. 셀은 아래 필드 atom(primitive)만 구독할 것.
  const instrumentAtom = atom((get) => get(snapshotSpecAtom)?.instrumentsKeyBySymbol?.[symbol]);

  // 표시에 필요한 instrument 필드는 primitive로 분리 노출 → 값이 같으면 Object.is dedup으로 전파가 끊긴다
  const pricePrecisionAtom = atom((get) => get(instrumentAtom)?.pricePrecision ?? 2);

  // 행 렌더 가능 여부. 포지션 청산/스냅샷 미로드 프레임에 행 전체를 null 처리하는 데 사용
  const isReadyAtom = atom((get) => Boolean(get(positionAtom) && get(instrumentAtom)));

  // TP/SL 셀이 identity(parentId)로만 쓰는 positionId — primitive라 행 수명 동안 안정적이다.
  // 통짜 position을 dedup해 내려줄 필요 없이 이 값만 넘기면 된다.
  const positionIdAtom = atom((get) => get(positionAtom)?.positionId);

  // --- 필드별 표시 atom (primitive라 값이 같으면 Object.is dedup으로 전파가 끊긴다) ---
  const sideAtom = atom((get) => get(positionAtom)?.side ?? 'Long');
  const marginTypeAtom = atom((get) => get(positionAtom)?.marginType ?? 'Cross');
  const quantityAtom = atom((get) => get(positionAtom)?.quantity ?? '0');
  const entryPriceAtom = atom((get) => get(positionAtom)?.avgEntryPrice ?? '0');
  const markPriceAtom = atom((get) => get(positionAtom)?.markPrice ?? '0');
  const liqPriceAtom = atom((get) => get(positionAtom)?.liquidationPrice);
  const takeProfitAtom = atom((get) => get(positionAtom)?.takeProfit ?? '0');
  const stopLossAtom = atom((get) => get(positionAtom)?.stopLoss ?? '0');
  const isAirdropAtom = atom((get) => (get(positionAtom)?.airdropType ?? null) !== null);
  const lockedUntilAtom = atom((get) => get(positionAtom)?.lockedUntil);
  const isBoostingAtom = atom((get) => (get(positionAtom)?.boostExpiryTimestamp ?? null) !== null);
  const boostCumPnlAtom = atom((get) => get(positionAtom)?.boostCumPnl ?? '0');
  const showProfitBoostBadgeAtom = atom((get) => {
    const position = get(positionAtom);
    if (!position) return false;
    return !get(isAirdropAtom) && (position.buyableBoosts.length > 0 || get(isBoostingAtom));
  });

  // --- PnL 표시값 (pnlMode × upnlBasis 설정 반영) ---
  // string으로 계산해 노출 → markPrice 등 표시 무관 필드만 바뀐 tick에는 값이 같아 전파가 끊긴다
  const displayPnlAtom = atom((get) => {
    const position = get(positionAtom);
    if (!position) return '0';
    return getDisplayPnl(position, { pnlMode: get(pnlModeAtom), upnlBasis: get(upnlBasisAtom) });
  });
  const displayPnlPercentAtom = atom((get) => {
    const position = get(positionAtom);
    if (!position) return '0';
    return getDisplayPnlPercent(position, { pnlMode: get(pnlModeAtom), upnlBasis: get(upnlBasisAtom) });
  });

  // 렌더 경로(useMemo)에서 하던 margin 계산을 데이터 레이어로 이동
  const marginAtom = atom((get) => {
    const position = get(positionAtom);
    return position ? calcPositionMargin(position) : '0';
  });

  // 설정(표시 타입)에 따른 레버리지 표시 문자열 — localStorage atom + 순수 함수로 계산
  const displayedLeverageTypeAtom = getLocalStorageAtom(
    'flipster.configs.v1.displayed_leverage_type',
    'positionLeverage',
  );
  const leverageDisplayAtom = atom((get) => formatLeverageDisplay(get(displayedLeverageTypeAtom), get(positionAtom)));

  // Qty/Value 셀 표시용 size. raw size(포지션×기준가)는 풀 정밀도라 표시 자릿수가 안 바뀌어도
  // 매 tick 값이 변한다. 셀의 표시 규칙(quote currency precision, no-round=버림)과 동일하게
  // 미리 양자화해, 표시 자릿수가 실제로 바뀐 tick에만 셀로 전파되게 한다.
  const displaySizeAtom = atom((get) => {
    const position = get(positionAtom);
    if (!position) return '0';
    const quoteCurrency = get(instrumentAtom)?.quoteCurrency;
    if (!quoteCurrency) return position.size;
    return Num(position.size).decimalPlaces(getQuoteCurrencyPrecision(quoteCurrency), BigNumber.ROUND_DOWN).toString();
  });

  return {
    symbol,
    slot,
    positionAtom,
    instrumentAtom,
    pricePrecisionAtom,
    isReadyAtom,
    positionIdAtom,
    sideAtom,
    marginTypeAtom,
    quantityAtom,
    entryPriceAtom,
    markPriceAtom,
    liqPriceAtom,
    takeProfitAtom,
    stopLossAtom,
    isAirdropAtom,
    lockedUntilAtom,
    isBoostingAtom,
    boostCumPnlAtom,
    showProfitBoostBadgeAtom,
    displayPnlAtom,
    displayPnlPercentAtom,
    marginAtom,
    leverageDisplayAtom,
    displaySizeAtom,
  };
});
openPositionRowDataBunja.debugLabel = 'openPositionRowDataBunja';
